import React from 'react';
import { Link } from 'react-router-dom';
import {
  Plane,
  Clock,
  Shield,
  Globe,
  CheckCircle2,
  Sparkles,
  AlertCircle,
  MapPin,
} from 'lucide-react';
import Footer from '../shared/Footer';
import cookieManager from '../../utils/cookieManager';

const usePageTitle = (title) => {
  React.useEffect(() => {
    if (title) document.title = title;
  }, [title]);
};

const highlights = [
  {
    title: '中文界面',
    desc: '完全中文化操作，对照泰国官网英文表单逐字段翻译，不会英文也能轻松填写。',
    icon: Globe,
  },
  {
    title: '隐私安全',
    desc: '数据最小化：信息仅用于当次自动填写；任务完成后会清理缓存与临时数据，避免长期留存。',
    icon: Shield,
  },
  {
    title: 'AI 自动填表',
    desc: 'AI 智能填表，自动完成泰国官网所有字段，准确率 98%，节省时间。',
    icon: Sparkles,
  },
  {
    title: '进度保存',
    desc: '自动保存填写进度，随时继续，避免重复输入，支持多次修改。',
    icon: Clock,
  },
  {
    title: '实时通知',
    desc: '实时显示自动化进度，验证码出现时桌面通知，全程透明可控。',
    icon: AlertCircle,
  },
  {
    title: '贴心服务',
    desc: '自动化失败积分退还；疑难场景提供咨询支持，确保顺利入境。',
    icon: CheckCircle2,
  },
];

const steps = [
  '准备护照信息、航班信息、泰国住宿地址，确认护照有效期 6 个月以上',
  '在我们的中文界面填写 6 大版块信息（个人、护照、航班、住宿、签证、健康申报）',
  '点击提交，AI 自动登录泰国官网填写所有表单字段，耗时约 5-10 分钟',
  '系统提取验证码图片，您在页面上填写验证码，提交后继续自动化',
  '机器人自动完成表单提交，您将收到入境卡邮件（如页面可提取到编号也会展示）',
  '建议保存邮件或截图，入境时按泰国移民要求出示即可',
];

const faqList = [
  {
    q: '什么是泰国入境卡？',
    a: 'TM.6 表格，入境泰国时必须填写的申报表。现在可以在线填写，入境时无需再填纸质版。',
  },
  {
    q: '需要准备哪些材料？',
    a: '护照信息、航班信息、泰国住宿地址、签证信息（如有）、过去两周访问的国家、健康状况。',
  },
  {
    q: '提交后还能修改吗？',
    a: '自动化开始后无法中止或修改。建议在点击"提交"前，仔细核对所有信息。如需修改，需等待本次完成后重新创建申请。',
  },
  {
    q: '什么是自动化填表？',
    a: '您在我们的中文界面填写信息后，AI 机器人会自动登录泰国官网，按照您填写的内容逐字段填入英文表单。相比手动填写：节省时间（5-10分钟 vs 30-60分钟）、降低错误（机器人不会填错字段）。',
  },
  {
    q: '为什么需要我填写验证码？',
    a: '泰国官网在表单提交前会出现验证码（Cloudflare Turnstile），这是防机器人措施。我们的系统会截取验证码图片显示给您，您输入后系统继续自动化。',
  },
  {
    q: '我的数据安全吗？会被保存吗？',
    a: '我们遵循数据最小化原则：仅在自动化填写过程中使用您提交的信息；任务完成后会清理缓存与临时数据。请不要在非必要字段填写多余隐私信息。',
  },
  {
    q: '收费多少？',
    a: '5 积分/次。泰国入境卡本身免费，我们仅收取智能填写服务费（价格已尽量压低）。',
  },
  {
    q: '自动化失败怎么办？',
    a: '如果自动化过程中出现异常（网络问题、官网维护等），系统会标记失败状态。积分会自动退还到您的账户，您可以稍后重新提交。',
  },
  {
    q: '入境时需要打印吗？',
    a: '建议保存入境参考号的截图或打印，入境时出示给泰国移民官员即可。',
  },
];

const ThaiVisaHome = () => {
  usePageTitle('旅报报·泰国入境卡助手 | 数据安全 · 注重隐私 · 低价 · AI 自动填表');
  const isLoggedIn = cookieManager.isLoggedIn();
  const heroCtaPath = isLoggedIn ? '/thai-arrival-card' : '/auth/register';
  const heroCtaLabel = isLoggedIn ? '开始填写' : '注册并获取积分';
  const bottomCtaLabel = isLoggedIn ? '前往入境卡表单' : '注册并获取积分';

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-amber-50 to-white">
      <header className="sticky top-0 z-40 backdrop-blur bg-white/70 border-b border-white/40">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="旅报报 logo" className="w-9 h-9" />
            <div className="font-extrabold text-gray-900">旅报报·泰国入境卡助手</div>
          </Link>
          <div className="flex items-center gap-3">
            <a
              href="https://lvbaobao.space/"
              className="inline-flex items-center px-3 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:border-orange-500 hover:text-orange-600 hover:bg-white transition-all"
            >
              <span className="sm:hidden">返回主站</span>
              <span className="hidden sm:inline">返回 lvbaobao.space</span>
            </a>
            <nav className="hidden md:flex items-center gap-5 text-sm text-gray-700">
              <a href="#features" className="hover:text-gray-900">产品能力</a>
              <a href="#flow" className="hover:text-gray-900">填写流程</a>
              <a href="#faq" className="hover:text-gray-900">FAQ</a>
              {isLoggedIn ? (
                <Link to="/home" className="px-4 py-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-500 text-white font-medium hover:from-orange-600 hover:to-amber-600 transition-all">
                  个人中心
                </Link>
              ) : (
                <Link to="/auth/login" className="px-4 py-2 rounded-lg border-2 border-gray-300 text-gray-700 font-medium hover:border-orange-500 hover:text-orange-600 transition-all">
                  登录
                </Link>
              )}
            </nav>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 opacity-70">
            <div className="absolute -top-20 -left-10 w-80 h-80 bg-orange-200 blur-[120px]" />
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-amber-200 blur-[120px]" />
          </div>
          <div className="relative max-w-6xl mx-auto px-4 py-14 grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white text-xs font-medium mb-4">
                <Plane className="w-4 h-4 mr-2" /> 智能填写
              </div>
              <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 leading-tight">
                泰国入境卡，AI 自动化填表服务
              </h1>
              <p className="mt-5 text-gray-700 text-lg md:text-xl">
                AI 自动化技术，替您完成泰国官网所有表单填写。全程中文引导，提交更快更稳；<strong>数据最小化</strong>、注重隐私，<strong>仅 5 积分/次</strong>。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to={heroCtaPath}
                  className="group inline-flex items-center justify-center px-6 py-4 rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 text-white border-2 border-orange-400 hover:scale-105 hover:shadow-lg transition-all duration-300 font-semibold"
                >
                  {heroCtaLabel}
                </Link>
                <a
                  href="#features"
                  className="inline-flex items-center justify-center px-6 py-4 rounded-2xl bg-white border-2 border-gray-200 text-gray-900 hover:border-orange-400 hover:shadow-lg transition-all duration-300 font-semibold"
                >
                  了解关键能力
                </a>
              </div>
              <div className="mt-8 flex items-center gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-orange-600" />
                  <span>5-10 分钟完成</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-orange-600" />
                  <span>数据最小化 · 注重隐私</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-orange-600" />
                  <span>5 积分/次</span>
                </div>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="rounded-[32px] bg-white/90 border border-orange-100 shadow-lg p-6 space-y-4">
              <div className="rounded-3xl bg-gradient-to-br from-orange-50 to-white border border-orange-100 p-6">
                <p className="text-sm text-orange-700 font-semibold">填写时间</p>
                <p className="mt-2 text-4xl font-black text-gray-900">5-10</p>
                <p className="text-sm text-gray-500">分钟</p>
              </div>
              <div className="rounded-3xl bg-gradient-to-br from-amber-50 to-white border border-amber-100 p-6">
                <p className="text-sm text-amber-700 font-semibold">积分消耗</p>
                <p className="mt-2 text-4xl font-black text-gray-900">5</p>
                <p className="text-sm text-gray-500">积分/次</p>
              </div>
              <div className="rounded-3xl bg-gradient-to-br from-orange-50 to-white border border-orange-100 p-6">
                <p className="text-sm text-orange-700 font-semibold">成功率</p>
                <p className="mt-2 text-4xl font-black text-gray-900">98%</p>
                <p className="text-sm text-gray-500">自动化成功</p>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4 text-center">核心能力</h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
              我们提供完整的泰国入境卡在线填写解决方案，让您的出行更加便捷
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {highlights.map((item, idx) => (
                <div key={idx} className="p-6 rounded-3xl border border-orange-100 hover:shadow-xl hover:border-orange-300 transition-all duration-300 bg-gradient-to-br from-white to-orange-50">
                  <item.icon className="w-12 h-12 text-orange-600 mb-4" />
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Flow Section */}
        <section id="flow" className="py-16 bg-gradient-to-b from-orange-50 to-white">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4 text-center">填写流程</h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
              简单 6 步，轻松完成泰国入境卡填写
            </p>
            <div className="space-y-4">
              {steps.map((step, idx) => (
                <div key={idx} className="flex gap-4 items-start">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white flex items-center justify-center font-bold text-lg">
                    {idx + 1}
                  </div>
                  <div className="flex-1 pt-1">
                    <p className="text-gray-900 font-medium">{step}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4 text-center">常见问题</h2>
            <p className="text-center text-gray-600 mb-12">
              关于泰国入境卡填写的常见问题解答
            </p>
            <div className="space-y-4">
              {faqList.map((item, idx) => (
                <details key={idx} className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-100 rounded-2xl p-6 hover:shadow-md transition-all duration-300 cursor-pointer group">
                  <summary className="font-semibold text-gray-900 flex items-center justify-between">
                    <span>{item.q}</span>
                    <span className="text-orange-600 group-open:rotate-180 transition-transform">▼</span>
                  </summary>
                  <p className="mt-4 text-gray-600 text-sm leading-relaxed">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-orange-500 to-amber-500">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
              准备好了吗？
            </h2>
            <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
              立即开始填写泰国入境卡，5-10 分钟完成，数据最小化更安心，仅 5 积分搞定
            </p>
            <Link
              to={heroCtaPath}
              className="inline-flex items-center justify-center px-8 py-4 rounded-2xl bg-white text-orange-600 font-bold hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              {bottomCtaLabel}
            </Link>
          </div>
        </section>
      </main>

      <Footer className="bg-white" />
    </div>
  );
};

export default ThaiVisaHome;
